/**
 * Pure health rules shared by the contract, the server and the web: option lists, withdrawal hold
 * math, label dose suggestions and how much stock a treatment uses. No DOM and no Worker APIs.
 *
 * Hondo records what the label or the veterinarian says. It never supplies a withdrawal interval or
 * a dose from general knowledge; a suggestion is always the product's own label value, shown as such.
 */
import { addDays, convertUnit, daysBetween, type Quantity } from "../../../contract";

// ---------------------------------------------------------------- vocabularies (values are stable API identifiers)

export const ROUTES = {
  im: "IM", sq: "SQ", oral: "Oral", topical: "Topical", pour_on: "Pour-on", intranasal: "Intranasal", iv: "IV", intramammary: "Intramammary",
} as const;
export type Route = keyof typeof ROUTES;

export const SITES = {
  neck: "Neck", shoulder: "Shoulder", hip: "Hip or rump", tail_head: "Tail head", ear: "Ear", back: "Along the back", leg: "Leg", udder: "Udder", other: "Other spot",
} as const;
export type Site = keyof typeof SITES;

/** Units a single dose can be given in. Rates (mL per 100 lb) live on the product label, not on what was given. */
export const DOSE_UNITS = ["ml", "cc", "mg", "g", "dose", "tablet", "bolus", "unit"] as const;

export const DIAGNOSIS = { observed: "What we saw", suspected: "Suspected", vet: "Vet's diagnosis" } as const;
export const WITHDRAWAL_SOURCE = { label: "Product label", vet: "Vet's instructions" } as const;

export const PROCEDURES = {
  castration: "Castration", dehorning: "Dehorning", branding: "Branding", implant: "Implant", ear_notch: "Ear notch", hoof_trim: "Hoof trim", other: "Other procedure",
} as const;
export type ProcedureKind = keyof typeof PROCEDURES;

export const METHODS = {
  band: "Band", knife: "Knife", burdizzo: "Burdizzo (clamp)", paste: "Paste", hot_iron: "Hot iron", scoop: "Scoop or surgical", freeze: "Freeze brand", tipping: "Tipping", other: "Other way",
} as const;

/** Which methods make sense for which procedure, so forms only offer sensible choices. */
export const METHODS_FOR: Partial<Record<ProcedureKind, Array<keyof typeof METHODS>>> = {
  castration: ["band", "knife", "burdizzo", "other"],
  dehorning: ["paste", "hot_iron", "scoop", "tipping", "other"],
  branding: ["hot_iron", "freeze", "other"],
};

export const SYMPTOMS = {
  cough: "Coughing", nasal: "Snotty nose", eye: "Runny or cloudy eye", lame: "Limping", swelling: "Swelling", scours: "Scours", bloat: "Bloat",
  off_feed: "Off feed", dull: "Droopy or dull", breathing: "Hard breathing", wound: "Cut or wound", other: "Something else",
} as const;
export const SEVERITY = { mild: "Mild", moderate: "Moderate", severe: "Severe" } as const;
export const SICK_STATUS = { sick: "Sick", improving: "Getting better", recovered: "Recovered" } as const;

export const TESTS = {
  bvd_pi: "BVD-PI", johnes: "Johne's", trich: "Trich", anaplasmosis: "Anaplasmosis", lepto: "Lepto", neospora: "Neospora", tb: "TB", brucellosis: "Brucellosis",
  fecal: "Fecal egg count", blood_panel: "Blood work", culture: "Culture", necropsy: "Necropsy", other: "Other test",
} as const;
export const SAMPLE_TYPES = {
  blood: "Blood", ear_notch: "Ear notch", fecal: "Manure", nasal_swab: "Nasal swab", milk: "Milk", tissue: "Tissue", preputial: "Preputial sample", semen: "Semen", other: "Other sample",
} as const;
export const RESULTS = { negative: "Negative", positive: "Positive", suspect: "Suspect", inconclusive: "Inconclusive", normal: "Normal", abnormal: "Abnormal" } as const;
export const RESULT_STATUS = { preliminary: "Preliminary", final: "Final", amended: "Amended" } as const;
export const BSE_CLASSES = { satisfactory: "Satisfactory", deferred: "Deferred", unsatisfactory: "Unsatisfactory", not_classified: "Not classified" } as const;
export const PASS_FAIL = { pass: "Sound", fail: "Problem found" } as const;

export const SICK_REASONS = ["Pinkeye", "Foot rot", "Pneumonia", "Scours", "Lice", "Worms", "Bloat", "Cut or wound"];

// ---------------------------------------------------------------- event data shapes

export interface TreatmentData {
  product_id?: string;
  product_name?: string;
  lot_id?: string;
  dose?: Quantity;
  route?: Route;
  site?: Site;
  reason?: string;
  diagnosis?: keyof typeof DIAGNOSIS;
  given_by?: string;
  vet_id?: string;
  temperature?: Quantity;
  dose_weight?: Quantity;
  meat_withdrawal_days?: number;
  milk_withdrawal_hours?: number;
  withdrawal_source?: keyof typeof WITHDRAWAL_SOURCE;
  booster_on?: string;
  recheck_on?: string;
  use?: Quantity;
  protocol?: string;
  notes?: string;
}

export interface ProcedureData {
  kind: ProcedureKind;
  method?: keyof typeof METHODS;
  brand?: string;
  product_id?: string;
  product_name?: string;
  lot_id?: string;
  meat_withdrawal_days?: number;
  given_by?: string;
  notes?: string;
}

export interface SicknessData {
  status?: keyof typeof SICK_STATUS;
  condition?: string;
  symptoms?: Array<keyof typeof SYMPTOMS>;
  severity?: keyof typeof SEVERITY;
  temperature?: Quantity;
  pulled?: boolean;
  pen_id?: string;
  recheck_on?: string;
  notes?: string;
}

export interface SampleData {
  test: keyof typeof TESTS;
  test_name?: string;
  sample_type?: keyof typeof SAMPLE_TYPES;
  sample_id?: string;
  lab_id?: string;
  collected_by?: string;
  reason?: string;
  notes?: string;
}

export interface LabResultData {
  test: keyof typeof TESTS;
  test_name?: string;
  result?: keyof typeof RESULTS;
  value?: number;
  unit?: string;
  status?: keyof typeof RESULT_STATUS;
  sample_id?: string;
  accession?: string;
  lab_id?: string;
  reported_on?: string;
  interpretation?: string;
  notes?: string;
}

export interface BseData {
  classification: keyof typeof BSE_CLASSES;
  scrotal?: Quantity;
  motility?: number;
  morphology?: number;
  primary_defects?: number;
  secondary_defects?: number;
  physical?: keyof typeof PASS_FAIL;
  vet_id?: string;
  examiner?: string;
  recheck_on?: string;
  notes?: string;
}

/** Product label values kept on medicine and vaccine stock items (stock_items.attrs). */
export interface ProductLabel {
  active_ingredient?: string;
  strength?: string;
  label_dose?: Quantity;
  label_dose_per?: Quantity;
  route?: Route;
  max_per_site?: Quantity;
  meat_withdrawal_days?: number;
  milk_withdrawal_hours?: number;
  booster_days?: number;
  storage?: string;
  maker?: string;
  label_notes?: string;
}

/** One product in a saved protocol or treatment preset. */
export interface ProtocolStep {
  event: "vaccination" | "treatment";
  product_id?: string;
  product_name: string;
  dose?: Quantity;
  /** When set, `dose` is per this much body weight (1 mL per 22 lb). */
  dose_per?: Quantity;
  route?: Route;
  site?: Site;
  /** Give again at the booster round. */
  booster?: boolean;
  /** Only for products not kept in supplies. Supplies use their label values. */
  meat_withdrawal_days?: number;
  milk_withdrawal_hours?: number;
}

export interface Protocol {
  id: string;
  name: string;
  description: string | null;
  kind: "round" | "treatment";
  reason: string | null;
  steps: ProtocolStep[];
  booster_days: number | null;
  recheck_days: number | null;
  version: number;
  active: boolean;
  updated_at: string;
}

// ---------------------------------------------------------------- the health facet

export interface HealthHold {
  kind: "meat" | "milk";
  /** First day the animal is clear. The hold is active while today < until. */
  until: string;
  product: string;
  product_id?: string;
  event_id: string;
  /** Date of the dose that set this hold (the last dose when a course was repeated). */
  on: string;
  days?: number;
  hours?: number;
  source?: keyof typeof WITHDRAWAL_SOURCE;
}

export interface HealthState {
  /** Holds that had not ended when the facet was computed. Readers compare with today: use `activeHolds`. */
  holds: HealthHold[];
  /** Products given recently with no withdrawal recorded. Never an all-clear. */
  unresolved: Array<{ product: string; product_id?: string; on: string; event_id: string }>;
  last_treatment?: { on: string; product: string; event_id: string };
  vaccinations: Array<{ product: string; product_id?: string; on: string; booster_on?: string; event_id: string }>;
  treatments: number;
  sick?: boolean;
  sickness?: { since: string; status: "sick" | "improving"; condition?: string; severity?: string; checked: string; recheck_on?: string; pulled?: boolean; event_id: string };
  procedures: Array<{ kind: ProcedureKind; on: string; method?: string; event_id: string }>;
  castrated_on?: string;
  bse?: { on: string; classification: keyof typeof BSE_CLASSES; scrotal?: Quantity; motility?: number; morphology?: number; recheck_on?: string; event_id: string };
  samples_waiting: Array<{ test: string; on: string; sample_id?: string; event_id: string }>;
  last_result?: { on: string; test: string; result?: string; value?: number; unit?: string; event_id: string };
}

/** How long a product given with no withdrawal recorded keeps showing as "withdrawal not recorded". */
export const UNRESOLVED_WINDOW_DAYS = 90;

export const productLabelOf = (data: { product_name?: string }, fallback = ""): string => data.product_name?.trim() || fallback;

/**
 * Holds created by one dose. Meat holds run whole days from the dose date; milk holds are given in
 * hours and are rounded up to whole days so a date-only record never releases early.
 */
export function holdsFromDose(e: { id: string; on: string; data: Pick<TreatmentData, "meat_withdrawal_days" | "milk_withdrawal_hours" | "withdrawal_source" | "product_id" | "product_name"> }): HealthHold[] {
  const out: HealthHold[] = [];
  if (!e.on) return out;
  const product = productLabelOf(e.data);
  const base = { product, product_id: e.data.product_id, event_id: e.id, on: e.on, source: e.data.withdrawal_source };
  const days = e.data.meat_withdrawal_days;
  if (typeof days === "number" && days > 0) out.push({ ...base, kind: "meat", until: addDays(e.on, days), days });
  const hours = e.data.milk_withdrawal_hours;
  if (typeof hours === "number" && hours > 0) out.push({ ...base, kind: "milk", until: addDays(e.on, Math.ceil(hours / 24)), hours });
  return out;
}

/** Holds still in force on a date (default: today). */
export function activeHolds(state: Pick<HealthState, "holds"> | null | undefined, on: string, kind?: "meat" | "milk"): HealthHold[] {
  return (state?.holds ?? []).filter((h) => h.until > on.slice(0, 10) && (!kind || h.kind === kind)).sort((a, b) => b.until.localeCompare(a.until));
}

/** The longest hold of a kind, which is the one that decides when the animal is clear. */
export function longestHold(state: Pick<HealthState, "holds"> | null | undefined, on: string, kind: "meat" | "milk"): HealthHold | null {
  return activeHolds(state, on, kind)[0] ?? null;
}

/** Month and day as numbers so templates read naturally in each language ("11/9" or "9/11"). */
export function monthDay(date: string): { month: number; day: number } {
  return { month: Number(date.slice(5, 7)), day: Number(date.slice(8, 10)) };
}

export function daysLeft(until: string, today: string): number {
  return Math.max(0, daysBetween(today, until));
}

// ---------------------------------------------------------------- doses

/** Round a suggested volume the way it is drawn: tenths for small doses, half mL above 10. */
export function roundDose(value: number): number {
  if (value <= 0) return 0;
  return value < 10 ? Math.round(value * 10) / 10 : Math.ceil(value * 2) / 2;
}

export interface DoseSuggestion {
  dose: Quantity;
  basis: "per_head" | "by_weight";
  /** The weight the suggestion used, when by weight. */
  weight?: Quantity & { on?: string };
  per?: Quantity;
}

/**
 * The label's own dose for one animal. Per-head labels need no weight; by-weight labels need a
 * weight and return null without one rather than guessing.
 */
export function suggestDose(label: { dose?: Quantity; per?: Quantity } | null | undefined, weight?: (Quantity & { on?: string }) | null): DoseSuggestion | null {
  if (!label?.dose || !(label.dose.value > 0)) return null;
  if (!label.per) return { dose: { ...label.dose }, basis: "per_head" };
  if (!weight || !(weight.value > 0) || !(label.per.value > 0)) return null;
  const w = convertUnit(weight.value, weight.unit, label.per.unit);
  if (w === null) return null;
  return { dose: { value: roundDose((w / label.per.value) * label.dose.value), unit: label.dose.unit }, basis: "by_weight", weight, per: label.per };
}

// ---------------------------------------------------------------- stock

const VOLUME = new Set(["ml", "cc", "l"]);
const MASS: Record<string, number> = { mg: 0.001, g: 1, kg: 1000 };
const COUNTED = new Set(["dose", "each", "head", "unit", "tablet", "bolus", "implant"]);

/** People type "mL", "doses" or "cc's" as a product's counting unit. */
export function normalizeUnit(unit: string | null | undefined): string {
  const u = (unit ?? "").trim().toLowerCase().replace(/['’]s$/, "").replace(/\.$/, "");
  if (["ml", "mls", "milliliter", "milliliters", "millilitre"].includes(u)) return "ml";
  if (["cc", "ccs"].includes(u)) return "cc";
  if (["dose", "doses", "dosis", "hd"].includes(u)) return "dose";
  if (["tablet", "tablets"].includes(u)) return "tablet";
  if (["bolus", "boluses"].includes(u)) return "bolus";
  if (["implant", "implants"].includes(u)) return "implant";
  if (["each", "ea", "unit", "units"].includes(u)) return u.startsWith("u") ? "unit" : "each";
  return u;
}

function convertAmount(value: number, from: string, to: string): number | null {
  const a = normalizeUnit(from);
  const b = normalizeUnit(to);
  if (a === b) return value;
  if (VOLUME.has(a) && VOLUME.has(b)) return convertUnit(value, a, b);
  if (a in MASS && b in MASS) return (value * MASS[a]) / MASS[b];
  return null;
}

export type StockUse = { amount: number; unit: string } | { amount: null; problem: "no_dose" | "unit_mismatch" };

/**
 * How much of a product a record used, in the product's own counting unit. An explicit total wins;
 * otherwise dose × head. Products counted in doses use one dose per head unless a dose count was given.
 */
export function stockUse(itemUnit: string, data: { dose?: Quantity; use?: Quantity }, head: number): StockUse {
  const unit = normalizeUnit(itemUnit);
  if (data.use && data.use.value > 0) {
    const amount = COUNTED.has(unit) && normalizeUnit(data.use.unit) === unit ? data.use.value : convertAmount(data.use.value, data.use.unit, unit);
    return amount === null ? { amount: null, problem: "unit_mismatch" } : { amount, unit: itemUnit };
  }
  if (head <= 0) return { amount: null, problem: "no_dose" };
  if (COUNTED.has(unit)) {
    const each = data.dose && normalizeUnit(data.dose.unit) === unit ? data.dose.value : 1;
    return { amount: each * head, unit: itemUnit };
  }
  if (!data.dose || !(data.dose.value > 0)) return { amount: null, problem: "no_dose" };
  const each = convertAmount(data.dose.value, data.dose.unit, unit);
  return each === null ? { amount: null, problem: "unit_mismatch" } : { amount: Math.round(each * head * 1000) / 1000, unit: itemUnit };
}

// ---------------------------------------------------------------- herd departures

/** Departure reasons that put an animal into the food chain or a sale, where a meat hold must stop it. */
const FOOD_REASONS = new Set(["sold", "sale", "sell", "slaughter", "slaughtered", "harvest", "harvested", "packer", "processed", "butcher", "butchered"]);

export function departureNeedsClearance(data: Record<string, unknown>): boolean {
  for (const key of ["reason", "kind", "purpose", "destination"]) {
    const v = data[key];
    if (typeof v === "string" && FOOD_REASONS.has(v.toLowerCase())) return true;
  }
  return false;
}
